import { useState } from "react";
import { Instagram } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NATURE_OPTIONS, HEARD_OPTIONS, MOVIE } from "@/constants/site";
import { submitInquiry } from "@/lib/api";

const initialState = {
  name: "",
  phone: "",
  email: "",
  company: "",
  nature: "",
  message: "",
  heard_about: "",
  website: "", // honeypot — real users leave this blank
};

export default function InquiryForm() {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showThanks, setShowThanks] = useState(false);

  const setField = (k) => (e) =>
    setValues((v) => ({ ...v, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !values.name.trim() ||
      !values.phone.trim() ||
      !values.email.trim() ||
      !values.nature ||
      !values.message.trim() ||
      !values.heard_about
    ) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(values);
      setValues(initialState);
      setShowThanks(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        data-testid="inquiry-form"
        noValidate
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name" required>
            <input
              type="text"
              className="fs-input"
              value={values.name}
              onChange={setField("name")}
              placeholder="Your full name"
              data-testid="inquiry-name-input"
              required
            />
          </Field>
          <Field label="Phone Number" required>
            <input
              type="tel"
              className="fs-input"
              value={values.phone}
              onChange={setField("phone")}
              placeholder="+1 555 000 0000"
              data-testid="inquiry-phone-input"
              required
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Email Address" required>
            <input
              type="email"
              className="fs-input"
              value={values.email}
              onChange={setField("email")}
              placeholder="you@company.com"
              data-testid="inquiry-email-input"
              required
            />
          </Field>
          <Field label="Company">
            <input
              type="text"
              className="fs-input"
              value={values.company}
              onChange={setField("company")}
              placeholder="Optional"
              data-testid="inquiry-company-input"
            />
          </Field>
        </div>

        <Field label="Nature of Inquiry" required>
          <Select value={values.nature} onValueChange={setField("nature")}>
            <SelectTrigger
              className="fs-input justify-between"
              data-testid="inquiry-nature-trigger"
            >
              <SelectValue placeholder="Choose one…" />
            </SelectTrigger>
            <SelectContent
              className="bg-fs-navy-2 border-white/10 text-fs-cream"
              data-testid="inquiry-nature-content"
            >
              {NATURE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt}
                  value={opt}
                  className="focus:bg-white/10 focus:text-fs-gold"
                  data-testid={`inquiry-nature-option-${opt
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Message" required>
          <textarea
            rows={5}
            className="fs-input resize-y"
            value={values.message}
            onChange={setField("message")}
            placeholder="Tell us a little about your inquiry…"
            data-testid="inquiry-message-input"
            required
          />
        </Field>

        <Field label="How you heard about us" required>
          <Select
            value={values.heard_about}
            onValueChange={setField("heard_about")}
          >
            <SelectTrigger
              className="fs-input justify-between"
              data-testid="inquiry-heard-trigger"
            >
              <SelectValue placeholder="Choose one…" />
            </SelectTrigger>
            <SelectContent
              className="bg-fs-navy-2 border-white/10 text-fs-cream"
              data-testid="inquiry-heard-content"
            >
              {HEARD_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt}
                  value={opt}
                  className="focus:bg-white/10 focus:text-fs-gold"
                  data-testid={`inquiry-heard-option-${opt
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {/* Honeypot — hidden from humans, visible to most bots. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          <label>
            Website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values.website}
              onChange={setField("website")}
              name="website"
              data-testid="inquiry-honeypot-input"
            />
          </label>
        </div>

        {error ? (
          <p
            className="text-sm text-red-400"
            data-testid="inquiry-form-error"
          >
            {error}
          </p>
        ) : null}

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="btn-fs-primary w-full sm:w-auto"
            data-testid="inquiry-submit-btn"
          >
            {loading ? "Sending…" : "Submit Inquiry"}
          </button>
        </div>
      </form>

      <Dialog open={showThanks} onOpenChange={setShowThanks}>
        <DialogContent
          className="bg-fs-navy-2 border-white/10 text-fs-cream"
          data-testid="inquiry-thanks-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-fs-gold">
              Thank you
            </DialogTitle>
            <DialogDescription className="text-fs-silver text-base leading-relaxed pt-2">
              Thank you for your enquiry. Someone from our team will reach out
              to you soon.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 rounded-xl border border-fs-orange/30 bg-fs-orange/[0.06] p-4 sm:p-5">
            <p className="text-xs uppercase tracking-[0.25em] text-fs-orange mb-2">
              While you wait
            </p>
            <p className="text-fs-cream/90 leading-relaxed">
              Join the hive on Instagram for behind-the-scenes art, character
              reveals, and squad updates as the film comes to life.
            </p>
            <a
              href={`https://instagram.com/${MOVIE.instagramHandle}`}
              target="_blank"
              rel="noreferrer"
              className="btn-fs-primary mt-4 !py-2.5 !px-5 !text-sm"
              data-testid="inquiry-thanks-instagram-btn"
            >
              <Instagram className="h-4 w-4" />
              Follow @{MOVIE.instagramHandle}
            </a>
          </div>

          <DialogFooter>
            <button
              type="button"
              className="btn-fs-ghost"
              onClick={() => setShowThanks(false)}
              data-testid="inquiry-thanks-close-btn"
            >
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-wider uppercase text-fs-silver mb-1.5 inline-block">
        {label}
        {required ? <span className="text-fs-orange ml-1">*</span> : null}
      </span>
      {children}
    </label>
  );
}
